import {useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {FormProvider, useForm} from 'react-hook-form';
import {
  usePayment,
  usePaymentSettings,
  useBuildTotalPayments,
  usePaymentProcessingState,
} from '../../hooks';
import {checkIfTotalAmountToPayZero} from '../../utils/payments';
import {StatusModalStates} from '@chekin/shared';
import {PAYMENT_PROVIDERS, PaymentsCart} from '@chekin/core';
import {useModalControls} from '@chekin/guestapp-ui-kit';
import visaIcon from 'assets/icons/visa-icon.svg?no-inline';
import mastercardIcon from 'assets/icons/mastercard-icon.svg?no-inline';
import SafeAndSecurePaymentInfo from '../common/SafeAndSecurePaymentInfo';
import PreAuthorization3DSInfoModal from './PreAuthorization3DSInfoModal';
import {StripePaymentForm} from './StripePaymentForm';
import {PaymentFormSkeleton} from './PaymentFormSkeleton';
import {PaymentProcessingModal} from '../PaymentProcessingModal';
import {Container, Form, Header, Hr, Price, StyledButton, TotalBox} from './styled';

const ProviderForms = {
  [PAYMENT_PROVIDERS.stripe]: StripePaymentForm,
  [PAYMENT_PROVIDERS.paycomet]: () => <></>,
  [PAYMENT_PROVIDERS.addon]: () => <></>,
};

type Props = {
  isLoading: boolean;
  onSuccess: () => void;
  paymentsCart?: PaymentsCart;
};
function PaymentForm({paymentsCart, isLoading, onSuccess}: Props) {
  const {t} = useTranslation();
  const [isFormReady, setIsFormReady] = useState(false);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const {
    isOpen: isPreAuthModalShown,
    closeModal: closePreAuthModal,
    openModal: openPreAuthModal,
  } = useModalControls();
  const {paymentProvider, currencySymbol} = usePaymentSettings();
  const methods = useForm({mode: 'onTouched'});
  const {handleSubmit} = methods;
  const {totalToPayNow} = useBuildTotalPayments({paymentsCart});
  const {
    processState,
    error,
    onPaymentSuccess,
    onPaymentError,
    setProcessState,
    closeProcessingModal,
  } = usePaymentProcessingState({
    onSuccess,
  });

  const payments = paymentsCart?.pay_now_payments;
  const isPaymentsAmountZero = checkIfTotalAmountToPayZero(paymentsCart);
  const isPayProcessDisabled =
    !isFormReady ||
    !isFormComplete ||
    !payments?.length ||
    isPaymentsAmountZero ||
    processState !== StatusModalStates.IDLE;

  const {pay} = usePayment({
    onPaymentExpired: closeProcessingModal,
    onPaymentSuccess: () => {
      closePreAuthModal();
      onPaymentSuccess();
    },
    onPaymentError: (error, errorMsg) => {
      closePreAuthModal();
      onPaymentError(error, errorMsg);
    },
  });

  const ProviderForm = useMemo(() => {
    return ProviderForms[paymentProvider];
  }, [paymentProvider]);

  const onSubmit = () => {
    if (isPayProcessDisabled) {
      return;
    }
    setProcessState(StatusModalStates.LOADING);
    openPreAuthModal();
    pay({payments});
  };

  return (
    <Container className="payment-form">
      <Header className="payment-form__header">
        <img width={40} height={24} src={mastercardIcon} alt="" />
        <img width={40} height={24} src={visaIcon} alt="" />
        <span>{t('credit_or_debit_card')}</span>
      </Header>
      <FormProvider {...methods}>
        <Form className="payment-form__form">
          {!isFormReady && <PaymentFormSkeleton />}

          <ProviderForm
            onFormReady={() => setIsFormReady(true)}
            onFormComplete={setIsFormComplete}
          />
        </Form>
      </FormProvider>
      <Hr />
      <TotalBox className="payment-form__total-box">
        <span>{t('total_payment')}</span>
        <Price $isLoaded={!isLoading} className="payment-form__price">
          {totalToPayNow ?? 0}
          {currencySymbol}
        </Price>
      </TotalBox>
      <SafeAndSecurePaymentInfo />
      <StyledButton
        label={t('confirm')}
        disabled={isPayProcessDisabled}
        onClick={handleSubmit(onSubmit)}
      />

      <PaymentProcessingModal
        isOpen={processState !== StatusModalStates.IDLE}
        close={closeProcessingModal}
        state={processState}
        error={error}
      />

      <PreAuthorization3DSInfoModal isOpen={isPreAuthModalShown} />
    </Container>
  );
}

export {PaymentForm};
